import { useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'

import config from 'constants/config'

export function getHolidaySynonymLabels(intl: IntlShape) {
  const isHoliday = config.brandConfig.holidaySynonym === 'holiday'

  return {
    yourHoliday: isHoliday
      ? intl.formatMessage({
          id: '3H8Y4yWGfkwTHd_nqL2d',
          defaultMessage: 'Your holiday',
          description: 'Title for sidebar filters modal when brand uses the holiday synonym',
        })
      : intl.formatMessage({
          id: 'qKqKh4kb_PBxqXcSc_0U',
          defaultMessage: 'Your escape',
          description: 'Title for sidebar filters modal when brand uses the escape synonym',
        }),

    yourNextHoliday: isHoliday
      ? intl.formatMessage({
          id: 'dIqeYE1ePNC8HQx1LfyQ',
          defaultMessage: 'Your Next Holiday',
          description: 'Share modal title base when brand uses the holiday synonym, before optional destination is appended',
        })
      : intl.formatMessage({
          id: '-tXSqlxQ1r6Vp-stG2pz',
          defaultMessage: 'Your Next Escape',
          description: 'Share modal title base when brand uses the escape synonym, before optional destination is appended',
        }),

    creditSnackbarMessage: isHoliday
      ? intl.formatMessage({
          id: '5FKBkYav24fBhi1OGZiK',
          defaultMessage: 'Use it towards your next holiday before it expires. Apply in checkout.',
          description: 'Credit snackbar body text encouraging user to spend credit on a holiday booking before expiry',
        })
      : intl.formatMessage({
          id: '4ZfKo7MjWD66I4PL0KVK',
          defaultMessage: 'Use it towards your next escape before it expires. Apply in checkout.',
          description: 'Credit snackbar body text encouraging user to spend credit on an escape booking before expiry',
        }),

    noUpcomingHolidays: isHoliday
      ? intl.formatMessage({
          id: 'kh4QZae-JGDE7lqaEL_6',
          defaultMessage: 'You have no upcoming holidays.',
          description: 'Empty state on insurance quote page when user has no holiday bookings to protect',
        })
      : intl.formatMessage({
          id: 'huQzEAK6-rkYkvu4Ppuj',
          defaultMessage: 'You have no upcoming escapes.',
          description: 'Empty state on insurance quote page when user has no escape bookings to protect',
        }),

    whichHolidayToProtect: isHoliday
      ? intl.formatMessage({
          id: 'j57bHiNY9mGi6F74leST',
          defaultMessage: 'Which holiday would you like to protect?',
          description: 'Insurance quote page prompt asking user to choose a holiday booking for travel protection',
        })
      : intl.formatMessage({
          id: 'fL4Egg-t0tMFt1SrSW6K',
          defaultMessage: 'Which escape would you like to protect?',
          description: 'Insurance quote page prompt asking user to choose an escape booking for travel protection',
        }),

    lookingForUpcoming: isHoliday
      ? intl.formatMessage({
          id: 'Dk1hM0ShIek5zFDouuG9',
          defaultMessage: 'Looking for upcoming holidays...',
          description: 'Loading message on insurance quote page while fetching holiday bookings',
        })
      : intl.formatMessage({
          id: 'S9sPpwXu-fzUrzaTGMEq',
          defaultMessage: 'Looking for upcoming escapes...',
          description: 'Loading message on insurance quote page while fetching escape bookings',
        }),

    customiseYourHoliday: isHoliday
      ? intl.formatMessage({
          id: 'IPgST_kgFbpyAQ4WdMjI',
          defaultMessage: 'Customise your holiday with these experiences',
          description: 'Checkout experiences upsell heading inviting user to add experiences to their holiday',
        })
      : intl.formatMessage({
          id: 'Nn14kCQ7RskGg2nVlG2l',
          defaultMessage: 'Customise your escape with these experiences',
          description: 'Checkout experiences upsell heading inviting user to add experiences to their escape',
        }),

    cannotPutOnHold: isHoliday
      ? intl.formatMessage({
          id: 'h8-Zr_6RYGiTgJH000s6',
          defaultMessage: 'Your holiday cannot be put on hold just yet, but we are currently working on making this possible so please check back shortly.',
          description: 'Error message when holiday on hold request fails (whitelabel brands)',
        })
      : intl.formatMessage({
          id: 'nnSSVo05fJYwXVRxJxBx',
          defaultMessage: 'Your escape cannot be put on hold just yet, but we are currently working on making this possible so please check back shortly.',
          description: 'Error message when escape on hold request fails (Luxury Escapes)',
        }),

    bookedBefore: isHoliday
      ? intl.formatMessage({
          id: 'nBJ_xpOaYOYbeUNfyLHM',
          defaultMessage: 'Looks like you’ve booked a holiday before!',
          description: 'Referee landing page heading shown to returning customers who previously booked a holiday',
        })
      : intl.formatMessage({
          id: 'o5kkULPl2LQE06JCYfp_',
          defaultMessage: 'Looks like you’ve booked an escape before!',
          description: 'Referee landing page heading shown to returning customers who previously booked an escape',
        }),
  }
}

function useHolidaySynonymLabels() {
  const intl = useIntl()

  return useMemo(() => getHolidaySynonymLabels(intl), [intl])
}

export default useHolidaySynonymLabels
